// src/api/selectTest.ts

/**
 * Selection API helpers for the Quantik Mind enterprise frontend.
 *
 * This module centralizes:
 * - request payloads for Quantum test selection
 * - response contracts for chaos and functional selection flows
 * - backend calls used to launch a new selection run
 *
 * The functions in this file rely on the shared API client for token handling,
 * request serialization, response parsing, and authentication error handling.
 */

import { api } from '../utils/apiClient';
import type { BusinessRiskMetrics } from './runs';

/**
 * Payload accepted by the chaos selection endpoint.
 */
export interface SelectTestsPayload {
  /** Identifier of the project for which the selection is executed. */
  project_id: string;

  /** Optional Prometheus endpoint override. */
  prometheus_url?: string;

  /** Observability lookback window used during runtime analysis. */
  lookback?: string;

  /** Chaos experiment types eligible for selection. */
  chaos_types?: string[];

  /** Optional subset of target services to evaluate. */
  target_services?: string[];

  /** Threshold parameter used by the backend selection engine. */
  tau?: number;

  /** Enables always-green filtering when supported by the backend. */
  apply_always_green?: boolean;

  /** Enables the Quantum optimizer stage when supported by the backend. */
  use_quantum_optimizer?: boolean;

  /** Optional per-service risk overrides. */
  risk_by_service?: Record<string, number>;

  /** Internal-only signal source override for diagnostics. */
  signal_override?: 'auto' | 'ignore_history' | 'ignore_observability' | 'full';
}

/**
 * Response returned by the chaos selection endpoint.
 */
export interface SelectTestsResponse {
  /** Identifier of the generated run. */
  run_id: string;

  /** Identifier of the project associated with the run. */
  project_id: string;

  /** Total number of experiments considered by the engine. */
  total_experiments: number;

  /** Number of experiments selected for execution. */
  selected_experiments: number;

  /** Percentage reduction achieved by the final selection. */
  reduction_percentage: number;

  /** Quantum Stability Index returned by the backend. */
  qsi: number;

  /** Confidence Index returned by the backend. */
  ci: number;

  /** Model Residual Risk Coverage returned by the backend. */
  mrr: number;

  /** Live Equation Score at the configured tau threshold. */
  eqs_live_at_tau: number;

  /** Mean Equation Score ratio. */
  eqs_ratio_mean: number;

  /** Optional estimated carbon savings. */
  carbon_saved_kg?: number;

  /** Optional number of avoided tests. */
  tests_avoided?: number;

  /** Probability distribution returned by the Quantum engine. */
  quantum_distribution: Record<string, number>;

  /** Number of avoided always-green tests. */
  always_green_avoided: number;

  /** Selected experiments returned by the backend. */
  selected: any[];

  /** Non-selected experiments returned by the backend. */
  not_selected: any[];

  signal_state?: string;
  has_experiment_library?: boolean;
  has_historical_signal?: boolean;
  has_runtime_signal?: boolean;
  signal_override?: string;
  effective_mode?: string;
}

/**
 * Trigger chaos-oriented Quantum selection for a project.
 *
 * Default selection parameters are applied unless explicitly overridden through
 * the optional custom payload.
 *
 * @param projectId - Identifier of the target project.
 * @param customPayload - Optional partial override for the default payload.
 * @returns Chaos selection response returned by the backend.
 */
export const fetchSelectedTests = async (
  projectId: string,
  customPayload?: Partial<SelectTestsPayload>
): Promise<SelectTestsResponse> => {
  const payload: SelectTestsPayload = {
    project_id: projectId,
    lookback: '10m',
    chaos_types: ['cpu-stress', 'pod-failure', 'network-delay', 'db-loss'],
    tau: 0.7,
    apply_always_green: true,
    use_quantum_optimizer: true,
    ...customPayload,
  };

  const data = await api.post<SelectTestsResponse>('/chaos/select', payload);
  return data;
};

/* =========================
   Functional response
========================= */

/**
 * Response returned by the functional selection endpoint.
 */
export interface FunctionalSelectResponse {
  /** Identifier of the generated run. */
  run_id: string;

  /** Identifier of the project associated with the run. */
  project_id: string;

  /** Threshold parameter used by the backend selection engine. */
  tau: number;
  selection_configuration?: {
    requested_tau?: number | null;
    effective_tau: number;
    tau_source: string;
    default_tau?: number;
    supported_range?: [number, number];
    semantics?: string;
    direction?: string;
  };

  /** Selection counts returned by the backend. */
  counts: {
    /** Total number of candidate tests. */
    total: number;

    /** Number of selected tests. */
    selected: number;
  };

  /** Selected functional tests returned by the backend. */
  selected: any[];

  /** Non-selected functional tests returned by the backend. */
  not_selected: any[];

  /** Percentage reduction achieved by the final selection. */
  reduction_percentage: number;
  business_metrics?: BusinessRiskMetrics;

  signal_state?: string;
  has_test_library?: boolean;
  has_sample_library?: boolean;
  has_real_library?: boolean;
  has_historical_signal?: boolean;
  has_runtime_signal?: boolean;
  sample_selection_count?: number;
  sample_selection_limit?: number;
  sample_limit_reached?: boolean;
  signal_override?: string;
  effective_mode?: string;
  library_mode?: 'sample' | 'real';
  always_green_tests_skipped?: number;
  redundant_tests_avoided?: number;
  dependency_aware_tests_added?: number;
  why_these_tests?: {
    title: string;
    bullets: string[];
    note?: string | null;
    sample_mode?: boolean;
  };
}

export interface FunctionalAsyncQueuedResponse {
  run_id: string;
  status: 'pending' | 'queued' | 'running';
  queued_at?: string | null;
  message?: string;
}

export type FunctionalSelectionResponse =
  | FunctionalSelectResponse
  | FunctionalAsyncQueuedResponse;

export const isFunctionalAsyncQueuedResponse = (
  response: FunctionalSelectionResponse
): response is FunctionalAsyncQueuedResponse => {
  const status = (response as FunctionalAsyncQueuedResponse)?.status;
  return (
    typeof (response as FunctionalAsyncQueuedResponse)?.run_id === 'string' &&
    (status === 'pending' || status === 'queued' || status === 'running') &&
    !('selected' in response)
  );
};

/* =========================
   Functional selection
========================= */

/**
 * Trigger functional Quantum selection for a project.
 *
 * @param projectId - Identifier of the target project.
 * @returns Functional selection response returned by the backend.
 */
export const fetchSelectedFunctionalTests = async (
  projectId: string,
  signalOverride: string = 'auto',
  tau?: number
): Promise<FunctionalSelectResponse> => {
  const params = new URLSearchParams({
    project_id: projectId,
    signal_override: signalOverride,
  });
  if (tau !== undefined) params.set('tau', String(tau));
  const data = await api.get<FunctionalSelectResponse>(
    `/functional/select?${params.toString()}`
  );

  return data;
};
